import React, { useState, useEffect, useCallback } from 'react';
import Typography from 'apollo-react/components/Typography';
import Box from 'apollo-react/components/Box';
import Paper from 'apollo-react/components/Paper';
import Button from 'apollo-react/components/Button';
import FileUpload from 'apollo-react/components/FileUpload';
import Download from 'apollo-react-icons/Download';
import StatusCheck from "apollo-react-icons/StatusCheck";
import axiosInstance from '../../../../../axiosInstance';
import { toast } from 'react-toastify';
import { useRequestComprehensiveDetails } from '../../../../../hooks/useRequestComprehensiveDetails';
import { useFileUpload } from '../../../../../hooks/useFileUpload';
import UploadProgress from '../../../../common/UploadProgress';
import { formatFileSize } from '../../../../../utils/formatFileSize';
import CommentToggle from '../../../../common/CommentToggle/CommentToggle';
import CommentsList from '../../../../common/FormApproveComponents/CommentsList/CommentsList';
import makeStyles from '@mui/styles/makeStyles';
import Avatar from 'apollo-react/components/Avatar';
import UserIcon from 'apollo-react-icons/User';

const styles = {
  commentButton: {
    border: '1px solid #0557D5 !important',
    padding: '8px 16px !important',
    fontFamily: `"Proxima Nova", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif !important`,
    fontSize: '14px !important',
    fontWeight: '500 !important',
    lineHeight: '1.5 !important',
    borderRadius: '4px !important',
  },
  commentButtonContainer: {
    display: 'flex',
    justifyContent: 'flex-end',
},
  profileSection: {
    display: 'flex',
    flexDirection: 'row',
    gap: 16,
    border: '1px solid #E6E6E6',
    padding: '16px 48px 16px 24px',
    borderRadius: '8px'
  },
  profileAvatar: {
    backgroundColor: '#0557D5 !important',
    color: '#fff'
  },
};

const useStyles = makeStyles(styles);

const LaboratoryUpload = ({ requestId, requestProgressId, persona = 'customer', comments=[],
  setComments = () => {}, onUploadSuccess, containerClassName = '', user = {} }) => {
  const classes = useStyles();

  const [existingFile, setExistingFile] = useState(null);
  const [isUpdatingRequest, setIsUpdatingRequest] = useState(false);
  
  const { requestDetails, loading } = useRequestComprehensiveDetails(requestProgressId, persona);
  const { uploadFile, uploading } = useFileUpload(persona !== 'customer', 'laboratory_file');

  const username = (user?.first_name && user?.last_name && `${user?.first_name} ${user?.last_name}`) ||
  (user?.username && `${user?.username}`) || '';
  const userEmail = user?.email ? `${user?.email}` : ''
  const userCategory = user?.end_users_category?.name || '';

  useEffect(() => {
    if (requestDetails?.request?.labFile) {
      // Normalize the file structure for our custom display
      const normalizedFile = {
        ...requestDetails.request.labFile,
        name: requestDetails.request.labFile.original_name || requestDetails.request.labFile.name,
        size: Number(requestDetails.request.labFile.file_size || requestDetails.request.labFile.size || 0),
        type: requestDetails.request.labFile.mime_type || requestDetails.request.labFile.type,
      };
      
      setExistingFile(normalizedFile);
    }
  }, [requestDetails]);

   const fetchComments = useCallback(async () => {
    let allComments = [];
    const getCommentsLink = persona === "customer" ? "enduser" : "";
    if(existingFile?.id)
    {
      const response = await axiosInstance.get(`/api/all-comments/${getCommentsLink}/lab_file/${existingFile?.id}`);
      allComments = response.data || [];
    }
    if (requestId && persona === "customer") {
      const responseCoreComments = await axiosInstance.get(`/api/core-validator-directed-committee-comments/core-comments-for-committee/${requestId}`);
      allComments = allComments.concat(responseCoreComments.data.map(c => {
        return {
          ...c,
          additional_file: c.uploaded_file || null,
          additional_file_id: c.uploaded_file_id || null,
          createdAt: c.createdAt,
          updatedAt: c.updatedAt,
          user: c.User || null,
          comment: c.comment,
          id: c.id,
        }
      }) || []);
    }
    setComments(allComments);
  }, [existingFile?.id, requestId, persona]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    fetchComments();
  }, [fetchComments]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleFileDownload = async (file) => {
    if (!file) return;
    
    try {
      const response = await axiosInstance.get(`/api/files/download/${file.id}`, {
        responseType: 'blob',
      });
      
      const blob = new Blob([response.data]);
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', file.original_name || file.name || 'laboratory-file');
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error downloading file:', error);
      toast.error('Failed to download file.', { position: 'top-center' });
    }
  };

  const updateRequestWithFileId = useCallback(async (fileId) => {

    setIsUpdatingRequest(true);
    try {
      const updateEndpoint = persona === 'customer' 
        ? `/api/requests/enduser/${requestId}`
        : `/api/requests/${requestId}`;
        
      const patchData = {
        lab_file_id: fileId
      };
      
      await axiosInstance.patch(updateEndpoint, patchData);
      
    } catch (error) {
      console.error('Error updating request with file ID:', error);
      toast.error('File uploaded but failed to link to request. Please try again.', {
        position: 'top-center',
      });
    } finally {
      setIsUpdatingRequest(false);
    }
  }, [requestId, persona]);

  const handleFileUpload = async (files) => {
    if (!files || files.length === 0) return;
    
    const file = files[0]; // Single file upload
    
    try {
      // Upload file using the existing hook
      const uploadedFile = await uploadFile(file);
      
      if (uploadedFile) {
        // Normalize file for custom display above
        const normalizedFile = {
          ...uploadedFile,
          name: uploadedFile.original_name || uploadedFile.name,
          size: Number(uploadedFile.file_size || uploadedFile.size || 0),
          type: uploadedFile.mime_type || uploadedFile.type,
        };
        
        setExistingFile(normalizedFile);
        
        // Update the request with the new file ID
        await updateRequestWithFileId(uploadedFile.id);
        
        // Notify parent component of successful upload
        if (onUploadSuccess) {
          onUploadSuccess();
        }
      }
    } catch (error) {
      console.error('Error uploading file:', error);
      // Error handling is already done by useFileUpload hook
    }
  };

  const handleAddComment = async (comment, subFormId, fileId = null, fileData = null) => {
    try {
      const addCommentLink = persona === "customer" ? "enduser" : "";
      const requestData = {
        resource_id: existingFile.id,
        comment,
      };

      // Add file ID if provided
      if (fileId) {
        requestData.fileId = fileId;
      }

      const response = await axiosInstance.post(`/api/all-comments/${addCommentLink}/lab_file`, requestData);

      // Enrich the response with file data if available
      // set isOwner to true since the current user just created this comment
      const enrichedComment = { ...response.data, isOwner: true };
      if (fileData && fileId) {
        // Convert fileData to the expected additional_file format
        enrichedComment.additional_file = {
          id: fileData.id,
          original_name: fileData.original_name,
          file_path: null, // Not provided by upload response
          file_size: fileData.file_size,
          mime_type: fileData.mime_type,
          createdAt: fileData.created_at,
          updatedAt: fileData.created_at,
          deletedAt: null,
          user_id: null, // Will be set by backend
          end_user_id: null, // Will be set by backend
          ...fileData // Include any additional fields
        };
      }

      console.log("Comment added with file data:", enrichedComment);
      setComments(prev => [...prev, enrichedComment]);
    } catch (error) {
      console.error("Error adding comment:", error);
      throw error;
    }
  };

  // handle edit comment
  const handleEditComment = async (commentId, newText, attachmentInfo = {}) => {
    try {
      const editLink = persona === "customer" ? "enduser" : "";
      const requestData = { comment: newText };

      if (attachmentInfo.remove_attachment) {
        requestData.remove_attachment = true;
      } else if (attachmentInfo.new_file_id) {
        requestData.additional_file_id = attachmentInfo.new_file_id;
      }

      await axiosInstance.put(`/api/all-comments/${editLink}/lab_file/${commentId}`, requestData);
      fetchComments();
    } catch (error) {
      console.error('error editing comment:', error);
      throw error;
    }
  };

  // handle delete comment
  const handleDeleteComment = async (commentId) => {
    try {
      const deleteLink = persona === "customer" ? "enduser" : "";
      await axiosInstance.delete(`/api/all-comments/${deleteLink}/lab_file/${commentId}`);
      setComments(prev => prev.filter(c => c.id !== commentId));
    } catch (error) {
      console.error('error deleting comment:', error);
      throw error;
    }
  };

  const isProcessing = uploading || isUpdatingRequest || loading;

  return (
    <div className={containerClassName}>
      <Box sx={{ marginBottom: 4 }}>
      <Typography
          variant="title1"
          component="h2"
          sx={{
            fontWeight: 500,
            marginBottom: 3,
            color: "#333",
            paddingBottom: 1,
          }}
        >
          {userCategory}
        </Typography>
        {username && userEmail &&
          <div className={classes.profileSection}>
            <Avatar className={classes.profileAvatar} stateless><UserIcon /></Avatar>
            <div>
              <Typography variant="subtitle" emphasis="high">
                {username}
              </Typography>
              <Typography variant="caption">
                {userEmail}
              </Typography>
            </div>
          </div>
        }

        {/* Show existing file info */}
        {existingFile && (
          <Box sx={{ 
            border: '1px solid #e0e0e0',
            borderRadius: 2,
            padding: 2,
            backgroundColor: '#f9f9f9',
            marginBottom: 3,
            marginTop: 3,
            display: 'flex',
            flexDirection: 'row',
            alignItems: 'center',
          }}>
            <Box>
            <Typography variant="body1" sx={{fontWeight: 500 }}>
              Current File: {existingFile.original_name || existingFile.name || 'Laboratory Document'}
            </Typography>
            
              <Typography variant="body2" color="text.secondary">
                File size: {existingFile.file_size ? formatFileSize(existingFile.file_size) : 'Unknown'}
              </Typography>

              <Typography variant="body2" color="text.secondary">
                Upload a new file to replace the existing one
              </Typography>
            </Box>

            <Button
                variant="outlined"
                size="small"
                endIcon={<Download />}
                onClick={() => handleFileDownload(existingFile)}
                sx={{ marginLeft: 'auto' }}
              >
                Download
            </Button>
          </Box>
        )}
        
        {/* File Upload Component */}
        <Box sx={{ marginBottom: 3, marginTop: 3 }}>
          <FileUpload
            value={[]}
            onUpload={handleFileUpload}
            onFileDelete={()=>{}}
            accept=".pdf,.doc,.docx,.zip,.rar,.tar,.gz,.7z"
            helperText="Please select your Laboratory document. Accepted file types: PDF, DOC, DOCX, ZIP, RAR, TAR, GZ, or 7Z files"
            fullWidth
            dropAreaHeight={100}
            multiple={false}
            maxFiles={1}
            required={true}
            disabled={isProcessing}
          />
          
          {/* Upload Progress */}
          <UploadProgress fieldName="laboratory_file" />
        </Box>

        {/* Status indicator */}
        <Box
          sx={{ 
            display: "flex", 
            alignItems: "center",
            padding: 2,
            backgroundColor: existingFile ? '#e8f5e8' : '#f5f5f5',
            borderRadius: 1,
            border: existingFile ? '1px solid #4caf50' : '1px solid #ddd',
          }}
        >
          {existingFile && (
            <StatusCheck size={20} color="#4caf50" style={{ marginRight: 8 }} />
          )}
          <Typography 
            variant="body2" 
            sx={{ 
              color: existingFile ? '#4caf50' : '#666',
              fontWeight: existingFile ? 'bold' : 'normal'
            }}
          >
            {existingFile
              ? `Laboratory document "${existingFile.original_name || existingFile.name}" is ready`
              : 'Please upload your Laboratory document to continue'
            }
          </Typography>
        </Box>
      </Box>
      <CommentsList
        prevComments={comments}
        pagination={{}}
        filters={{}}
        updateFilters={() => { }}
        persona={persona}
        showControls={false}
        showFilters={false}
        showSorting={false}
        showPageSize={false}
        showResultsInfo={false}
        showPagination={false}
        showCommentsTitle={false}
        onEdit={handleEditComment}
        onDelete={handleDeleteComment}
      />
      {existingFile && persona === "customer" && (
        <CommentToggle
          onAddComment={handleAddComment}
          persona={persona}
          buttonText="Comment"
          // buttonProps={{ variant: "primary" }}
          hasComments={true}
          // buttonContainerProps={{className: classes.commentButtonContainer}}
          hideCancelButton={false}
          hideTitleSeparator={true}
        />
      )}
    </div>
  );
};

export default LaboratoryUpload;